import DOMPurify from 'dompurify';

const DEFAULT_ALLOWED_DOMAINS = [
  'youtube.com',
  'youtube-nocookie.com',
  'youtu.be',
  'vimeo.com',
  'twitch.tv',
  'dailymotion.com',
  'dai.ly',
  'soundcloud.com',
  'sndcdn.com',
  'spotify.com',
  'bandcamp.com',
  'mixcloud.com',
  'loom.com',
  'wistia.com',
  'wistia.net',
  'vidyard.com',
  'streamable.com',
  'music.apple.com',
  'audiomack.com',
  'anchor.fm',
  'podbean.com',
];

const WARNING_STYLES = {
  color: 'red',
  fontWeight: 'bold',
};

const SANITIZER_CONFIG = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src'],
};

/**
 * Creates a warning element with consistent styling
 * @param {string} message - Warning message to display
 * @returns {HTMLElement} Warning paragraph element
 */
function createWarning(message) {
  // Additional safety: limit message length and sanitize
  const safeMessage = String(message).slice(0, 200);
  
  const warning = document.createElement('p');
  Object.assign(warning.style, WARNING_STYLES);
  warning.textContent = safeMessage;
  return warning;
}

/**
 * Sanitizes HTML content with iframe domain restrictions
 * @param {string} dirty - Unsanitized HTML string
 * @param {string[]} allowedDomains - Array of allowed domain patterns
 * @returns {string} Sanitized HTML with warnings for blocked content
 * @throws {TypeError} If input parameters are invalid
 */
// bearer:disable javascript_lang_dangerous_insert_html
export function sanitizeHtmlWithIframeRestriction(dirty, allowedDomains = DEFAULT_ALLOWED_DOMAINS) {
  // Input validation
  if (typeof dirty !== 'string') {
    throw new TypeError('Expected string input for sanitization');
  }
  
  if (!Array.isArray(allowedDomains)) {
    throw new TypeError('allowedDomains must be an array');
  }

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'iframe') {
      const src = node.getAttribute('src');
      
      if (!src) {
        node.replaceWith(createWarning('Iframe removed: No source URL provided'));
        return;
      }
      
      try {
        const url = new URL(src);
        const isAllowed = allowedDomains.some(domain => {
          return url.hostname === domain || 
                 url.hostname.endsWith('.' + domain);
        });
        
        if (!isAllowed) {
          node.replaceWith(createWarning(`Iframe blocked: Domain "${url.hostname}" is not allowed`));
        }
      } catch (e) {
        node.replaceWith(createWarning(`Iframe removed: Invalid URL "${src}"`));
      }
    }
  });

  const clean = DOMPurify.sanitize(dirty, SANITIZER_CONFIG);
  DOMPurify.removeHook('uponSanitizeElement');
  
  return clean;
}